import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  autoUpdate, flip, FloatingFocusManager, FloatingPortal, offset, shift, size,
  useClick, useDismiss, useFloating, useInteractions, useRole,
} from '@floating-ui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { BulkSelectionLimitNotice, type BulkActionBarProps } from './BulkActionBar';
import type { BulkAction } from './DataTableTypes';

interface ActionProps<T> {
  action: BulkAction<T>;
  selectedIds: string[];
  loading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function FloatingBulkAction<T>({ action, selectedIds, loading, open, onOpenChange }: ActionProps<T>) {
  const submenuItems = action.getSubmenu?.(selectedIds) ?? action.submenu;
  const actionDisabled = action.isDisabled?.(selectedIds) ?? false;
  const disabled = loading || actionDisabled;
  // IssuesPage.tsx:614 also sets loading during background list refreshes.
  // Keep an open menu in place; disable its actions until the refresh settles.
  const isOpen = open && !!submenuItems;
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: 'top-start',
    middleware: [offset(4), flip(), shift({ padding: 8 }), size({
      padding: 8,
      apply({ availableHeight, elements }) {
        elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
      },
    })],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context, { enabled: !!submenuItems && !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        className={`btn btn-sm whitespace-nowrap ${action.destructive
          ? 'text-red-600 hover:bg-red-50'
          : 'text-gray-600 hover:bg-gray-100/60 hover:text-gray-900'}`}
        disabled={disabled}
        title={actionDisabled ? action.title : undefined}
        {...(submenuItems ? getReferenceProps({
          onKeyDown(event: React.KeyboardEvent) {
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              event.preventDefault();
              onOpenChange(true);
            }
          },
        }) : { onClick: () => { onOpenChange(false); void action.onClick(selectedIds); } })}
      >
        <span className="text-cap-trim">{action.label}</span>
        {submenuItems && <HugeiconsIcon icon={ArrowDown01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />}
      </button>
      {isOpen && submenuItems && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 w-max min-w-[160px] max-w-[calc(100vw-1rem)] overflow-y-auto"
              {...getFloatingProps({
                onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
                  const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
                  if (!keys.includes(event.key)) return;
                  event.preventDefault();
                  const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
                  if (!items.length) return;
                  const current = items.indexOf(document.activeElement as HTMLButtonElement);
                  const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
                    : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
                  items[next].focus();
                },
              })}
            >
              {submenuItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 disabled:text-gray-300 disabled:cursor-not-allowed"
                  disabled={item.disabled || disabled}
                  title={item.disabled ? item.title : undefined}
                  onClick={() => { onOpenChange(false); item.onClick(); }}
                >
                  <span className="text-cap-trim block">{item.label}</span>
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

export function FloatingBulkActionBar<T>({ selectedCount, actions, selectedIds, loading = false, overSelectionLimit = false, onSelectFirstAllowed }: BulkActionBarProps<T>) {
  const [openAction, setOpenAction] = useState<string | null>(null);
  const [height, setHeight] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);

  // Reserve the rendered bar's height below the table, including when its
  // actions wrap. The last row can then scroll completely above the bar.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const measure = () => setHeight(bar.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div aria-hidden="true" style={{ height: `calc(${height}px + 2rem + env(safe-area-inset-bottom, 0px))` }} />
      {createPortal(
        <div className="fixed left-4 right-20 sm:right-4 bottom-4 z-40 flex justify-center pointer-events-none" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div
            ref={barRef}
            role="toolbar"
            aria-label={`${selectedCount} items selected`}
            className="pointer-events-auto flex flex-wrap items-center justify-center gap-1 max-w-full px-2 py-2 bg-white rounded-xl shadow-xl border border-gray-200"
          >
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 whitespace-nowrap" aria-live="polite">
              {selectedCount} selected
            </span>
            {overSelectionLimit && <span className="px-2"><BulkSelectionLimitNotice onSelectFirstAllowed={onSelectFirstAllowed} /></span>}
            <span className="w-px h-5 bg-gray-200 mx-1" aria-hidden="true" />
            {actions.map((action, index) => (
              <Fragment key={action.label}>
                {action.destructive && index > 0 && !actions[index - 1].destructive && <span className="w-px h-5 bg-gray-200 mx-1" aria-hidden="true" />}
                <FloatingBulkAction
                  action={action}
                  selectedIds={selectedIds}
                  loading={loading || overSelectionLimit}
                  open={openAction === action.label}
                  onOpenChange={(open) => setOpenAction(open ? action.label : null)}
                />
              </Fragment>
            ))}
          </div>
        </div>, document.body,
      )}
    </>
  );
}
